/* eslint-disable react/no-danger, jsx-a11y/no-noninteractive-element-interactions */
import * as React from 'react';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { formStyles } from 'styles/formStyles';
import classnames from 'classnames';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import moment from 'moment-timezone';
import { formatDecimal, formatMoney, formatPercent } from 'utils/formatter';
import { uniq } from 'lodash';
import { ChevronDownIcon, ChevronUpIcon } from '@fluentui/react-icons';
import { GrowthWithColor, getAvg, getGrowthWithColor } from 'reports/utils';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth/selector';
import { lookupsLoadingFinishedSelector } from 'store/lookups/selector';
import { UmsatzSummaryProps, useUmsatzSummary } from './hooks/useUmsatzSummary';
import { defaultLineData, FilterValues, groupByOptions, LineData, LineDataWithGrowth, sourceOptions } from './types';
import { FilterInfo } from './FilterInfo';

const theme: ITheme = getTheme();
const { palette } = theme;

const odd = palette.neutralLight;
const even = palette.neutralLighterAlt;

export const styles = mergeStyleSets({
    container: {
        width: '100%',
        // height: 'calc(100vh - 120px)',
        overflowY: 'auto',
    },
    printContainer: {
        width: '100%',
        height: '100%',
        fontSize: '12px',
    },
    tdHead: {
        fontWeight: 700,
        paddingLeft: 2,
        paddingRight: 2,
        cursor: 'pointer',
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },
    tdBody: {
        paddingLeft: 2,
        paddingRight: 2,
        borderBottom: '0.5px solid #CCC',
        // eslint-disable-next-line no-useless-computed-key
        ['-webkit-print-color-adjust']: 'exact',
    },

    borderTop: {
        borderTop: '1px solid',
    },
    strongBorderTop: {
        borderTop: '3px solid',
    },
    borderBottom: {
        borderBottom: '1px solid',
    },
    strongBorderBottom: {
        borderBottom: '3px solid',
    },
    noPadding: {
        paddingTop: 0,
        paddingBottom: 0,
    },
    auNr: {},
    count: {
        backgroundColor: odd,
    },
    ekNetto: {
        backgroundColor: even,
    },
    vkNetto: {
        backgroundColor: odd,
    },
    bMarge: {
        backgroundColor: even,
    },
    nMarge: {
        backgroundColor: odd,
    },
    bAnteil: {
        backgroundColor: even,
    },
    fAnteil: {
        backgroundColor: odd,
    },
    fAnteilTotal: {
        backgroundColor: odd,
        fontWeight: 700,
    },
    borderRight: {
        borderRight: '1px solid',
    },
    note: {
        fontSize: 12,
        color: palette.neutralTertiary,
    },
});

interface UmsatzuebersichtProps extends FilterValues {
    printView?: boolean;
    toggleSort: (column: string) => void;
}

export const Umsatzuebersicht: React.FC<UmsatzuebersichtProps> = (filterValues) => {
    const lookupsLoadingFinished = useSelector(lookupsLoadingFinishedSelector);
    const auth = useSelector(authSelector);

    const {
        source,
        groupBy,
        datumVon,
        datumBis,
        addMissingRows1,
        addMissingRows3,
        printView = false,
        sortColumn,
        sortDirection,
        toggleSort,
    } = filterValues;

    const umsatzSummaryPropsJ0 = React.useMemo<UmsatzSummaryProps>(
        () => ({
            ...filterValues,
            enabled: true,
        }),
        [filterValues],
    );

    const umsatzSummaryJ0 = useUmsatzSummary(umsatzSummaryPropsJ0);

    const umsatzSummaryPropsJ1 = React.useMemo<UmsatzSummaryProps>(
        () => ({
            ...filterValues,
            datumVon: moment(datumVon).subtract({ years: 1 }).format('YYYY-MM-DD'),
            datumBis: moment(datumBis).subtract({ years: 1 }).format('YYYY-MM-DD'),
            enabled: !!addMissingRows1,
        }),
        [filterValues, datumVon, datumBis, addMissingRows1],
    );

    const umsatzSummaryJ1 = useUmsatzSummary(umsatzSummaryPropsJ1);

    const umsatzSummaryPropsJ2 = React.useMemo<UmsatzSummaryProps>(
        () => ({
            ...filterValues,
            datumVon: moment(datumVon).subtract({ years: 2 }).format('YYYY-MM-DD'),
            datumBis: moment(datumBis).subtract({ years: 2 }).format('YYYY-MM-DD'),
            enabled: !!addMissingRows3,
        }),
        [filterValues, datumVon, datumBis, addMissingRows3],
    );

    const umsatzSummaryJ2 = useUmsatzSummary(umsatzSummaryPropsJ2);

    const umsatzSummaryPropsJ3 = React.useMemo<UmsatzSummaryProps>(
        () => ({
            ...filterValues,
            datumVon: moment(datumVon).subtract({ years: 3 }).format('YYYY-MM-DD'),
            datumBis: moment(datumBis).subtract({ years: 3 }).format('YYYY-MM-DD'),
            enabled: !!addMissingRows3,
        }),
        [filterValues, datumVon, datumBis, addMissingRows3],
    );

    const umsatzSummaryJ3 = useUmsatzSummary(umsatzSummaryPropsJ3);

    const uniqGroupByIds = React.useMemo<string[]>(() => {
        if (umsatzSummaryJ0.loading || umsatzSummaryJ1.loading || umsatzSummaryJ2.loading || umsatzSummaryJ3.loading) {
            return [];
        }

        return uniq([
            ...umsatzSummaryJ0.lineDataList.map((lineData) => lineData.groupById),
            ...(addMissingRows1 ? umsatzSummaryJ1.lineDataList.map((lineData) => lineData.groupById) : []),
            ...(addMissingRows3 ? umsatzSummaryJ2.lineDataList.map((lineData) => lineData.groupById) : []),
            ...(addMissingRows3 ? umsatzSummaryJ3.lineDataList.map((lineData) => lineData.groupById) : []),
        ]);
    }, [
        umsatzSummaryJ0.loading,
        umsatzSummaryJ0.lineDataList,
        umsatzSummaryJ1.loading,
        umsatzSummaryJ1.lineDataList,
        umsatzSummaryJ2.loading,
        umsatzSummaryJ2.lineDataList,
        umsatzSummaryJ3.loading,
        umsatzSummaryJ3.lineDataList,
        addMissingRows1,
        addMissingRows3,
    ]);

    const umsatzSummaryLine = React.useMemo<LineDataWithGrowth>(() => {
        const totalAvg: LineData = { ...defaultLineData };

        const umsatzSummaries = [umsatzSummaryJ1, umsatzSummaryJ2, umsatzSummaryJ3];

        totalAvg.count = getAvg(umsatzSummaries.map((s) => s.total.count));
        totalAvg.ekNetto = getAvg(umsatzSummaries.map((s) => s.total.ekNetto));
        totalAvg.vkNetto = getAvg(umsatzSummaries.map((s) => s.total.vkNetto));
        totalAvg.bMarge = getAvg(umsatzSummaries.map((s) => s.total.bMarge));
        totalAvg.nMarge = getAvg(umsatzSummaries.map((s) => s.total.nMarge));
        totalAvg.bAnteil = getAvg(umsatzSummaries.map((s) => s.total.bAnteil));
        totalAvg.fAnteil = getAvg(umsatzSummaries.map((s) => s.total.fAnteil));

        return {
            ...umsatzSummaryJ0.total,
            groupById: '-1',
            groupByLabel: 'Gesamt (' + uniqGroupByIds.length + ')',
            countPrevGrowth: getGrowthWithColor(umsatzSummaryJ0.total.count, umsatzSummaryJ1.total.count),
            countAvgGrowth: getGrowthWithColor(umsatzSummaryJ0.total.count, totalAvg.count),
            countPPrevGrowth: getGrowthWithColor(umsatzSummaryJ0.total.countP, umsatzSummaryJ1.total.countP),
            countPAvgGrowth: getGrowthWithColor(umsatzSummaryJ0.total.countP, totalAvg.countP),
            ekNettoPrevGrowth: getGrowthWithColor(umsatzSummaryJ0.total.ekNetto, umsatzSummaryJ1.total.ekNetto),
            ekNettoAvgGrowth: getGrowthWithColor(umsatzSummaryJ0.total.ekNetto, totalAvg.ekNetto),
            ekNettoPPrevGrowth: getGrowthWithColor(umsatzSummaryJ0.total.ekNettoP, umsatzSummaryJ1.total.ekNettoP),
            ekNettoPAvgGrowth: getGrowthWithColor(umsatzSummaryJ0.total.ekNettoP, totalAvg.ekNettoP),
            vkNettoPrevGrowth: getGrowthWithColor(umsatzSummaryJ0.total.vkNetto, umsatzSummaryJ1.total.vkNetto),
            vkNettoAvgGrowth: getGrowthWithColor(umsatzSummaryJ0.total.vkNetto, totalAvg.vkNetto),
            vkNettoPPrevGrowth: getGrowthWithColor(umsatzSummaryJ0.total.vkNettoP, umsatzSummaryJ1.total.vkNettoP),
            vkNettoPAvgGrowth: getGrowthWithColor(umsatzSummaryJ0.total.vkNettoP, totalAvg.vkNettoP),
            bMargePrevGrowth: getGrowthWithColor(umsatzSummaryJ0.total.bMarge, umsatzSummaryJ1.total.bMarge),
            bMargeAvgGrowth: getGrowthWithColor(umsatzSummaryJ0.total.bMarge, totalAvg.bMarge),
            bMargePPrevGrowth: getGrowthWithColor(umsatzSummaryJ0.total.bMargeP, umsatzSummaryJ1.total.bMargeP),
            bMargePAvgGrowth: getGrowthWithColor(umsatzSummaryJ0.total.bMargeP, totalAvg.bMargeP),
            nMargePrevGrowth: getGrowthWithColor(umsatzSummaryJ0.total.nMarge, umsatzSummaryJ1.total.nMarge),
            nMargeAvgGrowth: getGrowthWithColor(umsatzSummaryJ0.total.nMarge, totalAvg.nMarge),
            nMargePPrevGrowth: getGrowthWithColor(umsatzSummaryJ0.total.nMargeP, umsatzSummaryJ1.total.nMargeP),
            nMargePAvgGrowth: getGrowthWithColor(umsatzSummaryJ0.total.nMargeP, totalAvg.nMargeP),
            bAnteilPrevGrowth: getGrowthWithColor(umsatzSummaryJ0.total.bAnteil, umsatzSummaryJ1.total.bAnteil),
            bAnteilAvgGrowth: getGrowthWithColor(umsatzSummaryJ0.total.bAnteil, totalAvg.bAnteil),
            fAnteilPrevGrowth: getGrowthWithColor(umsatzSummaryJ0.total.fAnteil, umsatzSummaryJ1.total.fAnteil),
            fAnteilAvgGrowth: getGrowthWithColor(umsatzSummaryJ0.total.fAnteil, totalAvg.fAnteil),
        };
    }, [uniqGroupByIds, umsatzSummaryJ0, umsatzSummaryJ1, umsatzSummaryJ2, umsatzSummaryJ3]);

    // const [sort, setSort] = React.useState({ column: 'groupByLabel', ascending: true });

    const unsortedTableData = React.useMemo<LineDataWithGrowth[]>(() => {
        return uniqGroupByIds.map((groupById) => {
            const lineDataJ0 =
                umsatzSummaryJ0.lineDataList.find((lineData) => lineData.groupById === groupById) || defaultLineData;

            const lineDataJ1 =
                umsatzSummaryJ1.lineDataList.find((lineData) => lineData.groupById === groupById) || defaultLineData;

            const lineDataJ2 =
                umsatzSummaryJ2.lineDataList.find((lineData) => lineData.groupById === groupById) || defaultLineData;

            const lineDataJ3 =
                umsatzSummaryJ3.lineDataList.find((lineData) => lineData.groupById === groupById) || defaultLineData;

            const lineDataAvgList = [lineDataJ1, lineDataJ2, lineDataJ3];

            const lineDataAvg = {
                ...defaultLineData,
                count: getAvg(lineDataAvgList.map((ld) => ld.count)),
                countP: getAvg(lineDataAvgList.map((ld) => ld.countP)),
                ekNetto: getAvg(lineDataAvgList.map((ld) => ld.ekNetto)),
                ekNettoP: getAvg(lineDataAvgList.map((ld) => ld.ekNettoP)),
                vkNetto: getAvg(lineDataAvgList.map((ld) => ld.vkNetto)),
                vkNettoP: getAvg(lineDataAvgList.map((ld) => ld.vkNettoP)),
                bMarge: getAvg(lineDataAvgList.map((ld) => ld.bMarge)),
                bMargeP: getAvg(lineDataAvgList.map((ld) => ld.bMargeP)),
                nMarge: getAvg(lineDataAvgList.map((ld) => ld.nMarge)),
                nMargeP: getAvg(lineDataAvgList.map((ld) => ld.nMargeP)),
                bAnteil: getAvg(lineDataAvgList.map((ld) => ld.bAnteil)),
                fAnteil: getAvg(lineDataAvgList.map((ld) => ld.fAnteil)),
            };

            return {
                ...lineDataJ0,
                groupById:
                    lineDataJ0.groupById || lineDataJ1.groupById || lineDataJ2.groupById || lineDataJ3.groupById || '0',
                groupByLabel: filterValues.hideGroupByLabel
                    ? ''
                    : lineDataJ0.groupByLabel ||
                      lineDataJ1.groupByLabel ||
                      lineDataJ2.groupByLabel ||
                      lineDataJ3.groupByLabel ||
                      '?',
                groupByNote:
                    lineDataJ0.groupByNote ||
                    lineDataJ1.groupByNote ||
                    lineDataJ2.groupByNote ||
                    lineDataJ3.groupByNote ||
                    '',
                groupByLink: filterValues.hideGroupByLabel
                    ? ''
                    : lineDataJ0.groupByLink ||
                      lineDataJ1.groupByLink ||
                      lineDataJ2.groupByLink ||
                      lineDataJ3.groupByLink ||
                      '',

                countPrevGrowth: getGrowthWithColor(lineDataJ0.count, lineDataJ1.count),
                countAvgGrowth: getGrowthWithColor(lineDataJ0.count, lineDataAvg.count),
                ekNettoPrevGrowth: getGrowthWithColor(lineDataJ0.ekNetto, lineDataJ1.ekNetto),
                ekNettoAvgGrowth: getGrowthWithColor(lineDataJ0.ekNetto, lineDataAvg.ekNetto),
                vkNettoPrevGrowth: getGrowthWithColor(lineDataJ0.vkNetto, lineDataJ1.vkNetto),
                vkNettoAvgGrowth: getGrowthWithColor(lineDataJ0.vkNetto, lineDataAvg.vkNetto),
                bMargePrevGrowth: getGrowthWithColor(lineDataJ0.bMarge, lineDataJ1.bMarge),
                bMargeAvgGrowth: getGrowthWithColor(lineDataJ0.bMarge, lineDataAvg.bMarge),
                nMargePrevGrowth: getGrowthWithColor(lineDataJ0.nMarge, lineDataJ1.nMarge),
                nMargeAvgGrowth: getGrowthWithColor(lineDataJ0.nMarge, lineDataAvg.nMarge),
                bAnteilPrevGrowth: getGrowthWithColor(lineDataJ0.bAnteil, lineDataJ1.bAnteil),
                bAnteilAvgGrowth: getGrowthWithColor(lineDataJ0.bAnteil, lineDataAvg.bAnteil),
                fAnteilPrevGrowth: getGrowthWithColor(lineDataJ0.fAnteil, lineDataJ1.fAnteil),
                fAnteilAvgGrowth: getGrowthWithColor(lineDataJ0.fAnteil, lineDataAvg.fAnteil),
            };
        });
    }, [
        uniqGroupByIds,
        umsatzSummaryJ0.lineDataList,
        umsatzSummaryJ1.lineDataList,
        umsatzSummaryJ2.lineDataList,
        umsatzSummaryJ3.lineDataList,
        filterValues.hideGroupByLabel,
    ]);

    const sortedTableData = React.useMemo<LineDataWithGrowth[]>(() => {
        if (['groupByLabel'].includes(sortColumn)) {
            return unsortedTableData.sort((a, b) =>
                sortDirection === 'asc'
                    ? ((a[sortColumn as keyof LineDataWithGrowth] as string) || '')
                          .toLocaleLowerCase()
                          .localeCompare((b[sortColumn as keyof LineDataWithGrowth] as string) || '')
                    : ((b[sortColumn as keyof LineDataWithGrowth] as string) || '')
                          .toLocaleLowerCase()
                          .localeCompare((a[sortColumn as keyof LineDataWithGrowth] as string) || ''),
            );
        }

        if (
            [
                'countPrevGrowth',
                'ekNettoPrevGrowth',
                'vkNettoPrevGrowth',
                'bMargePrevGrowth',
                'tippProvPrevGrowth',
                'skontoPrevGrowth',
                'frachtPrevGrowth',
                'nMargePrevGrowth',
                'bAnteilPrevGrowth',
                'fAnteilPrevGrowth',
            ].includes(sortColumn)
        ) {
            return unsortedTableData.sort((a, b) =>
                sortDirection === 'asc'
                    ? ((a[sortColumn as keyof LineDataWithGrowth] as GrowthWithColor)?.growth || 0) -
                      ((b[sortColumn as keyof LineDataWithGrowth] as GrowthWithColor)?.growth || 0)
                    : ((b[sortColumn as keyof LineDataWithGrowth] as GrowthWithColor)?.growth || 0) -
                      ((a[sortColumn as keyof LineDataWithGrowth] as GrowthWithColor)?.growth || 0),
            );
        }

        return unsortedTableData.sort((a, b) =>
            sortDirection === 'asc'
                ? ((a[sortColumn as keyof LineDataWithGrowth] as number) || 0) -
                  ((b[sortColumn as keyof LineDataWithGrowth] as number) || 0)
                : ((b[sortColumn as keyof LineDataWithGrowth] as number) || 0) -
                  ((a[sortColumn as keyof LineDataWithGrowth] as number) || 0),
        );
    }, [unsortedTableData, sortColumn, sortDirection]);

    const renderSortIcon = (column: string) => {
        if (sortColumn === column) {
            if (sortDirection === 'asc') {
                return <ChevronUpIcon className={formStyles.reportSortIcon} />;
            }
            return <ChevronDownIcon className={formStyles.reportSortIcon} />;
        }
        return null;
    };

    const renderHeaderRow = () => {
        const moneyClassName = classnames(
            styles.tdHead,
            formStyles.dataValueRight,
            printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
        );

        const percentClassName = classnames(
            styles.tdHead,
            formStyles.dataValueRight,
            printView ? formStyles.reportPercentPrintCell : formStyles.reportPercentCell,
        );

        return (
            <tr>
                <td className={classnames(styles.tdHead)} onClick={() => toggleSort('groupByLabel')}>
                    Name
                    {renderSortIcon('groupByLabel')}
                </td>
                <td
                    className={classnames(percentClassName, styles.count)}
                    onClick={() => toggleSort('countPrevGrowth')}
                >
                    {renderSortIcon('countPrevGrowth')}%
                </td>
                <td className={classnames(percentClassName, styles.count)} onClick={() => toggleSort('countP')}>
                    {renderSortIcon('countP')}
                    Anteil
                </td>
                <td
                    className={classnames(moneyClassName, styles.count, styles.borderRight)}
                    onClick={() => toggleSort('count')}
                >
                    {renderSortIcon('count')}
                    {source === 'auftrag' ? 'Anz. Au.' : 'Anz. Re.'}
                </td>
                {filterValues.showEkNetto && filterValues.source !== 'bestellung' && (
                    <>
                        <td
                            className={classnames(percentClassName, styles.ekNetto)}
                            onClick={() => toggleSort('ekNettoPrevGrowth')}
                        >
                            {renderSortIcon('ekNettoPrevGrowth')}%
                        </td>
                        <td
                            className={classnames(moneyClassName, styles.ekNetto)}
                            onClick={() => toggleSort('ekNettoP')}
                        >
                            {renderSortIcon('ekNettoP')}
                            Anteil
                        </td>
                        <td
                            className={classnames(moneyClassName, styles.ekNetto)}
                            onClick={() => toggleSort('ekNetto')}
                        >
                            {renderSortIcon('ekNetto')}
                            EK Netto
                        </td>
                    </>
                )}
                {filterValues.showVkNetto && (
                    <>
                        <td
                            className={classnames(percentClassName, styles.vkNetto)}
                            onClick={() => toggleSort('vkNettoPrevGrowth')}
                        >
                            {renderSortIcon('vkNettoPrevGrowth')}%
                        </td>
                        <td
                            className={classnames(moneyClassName, styles.vkNetto)}
                            onClick={() => toggleSort('vkNettoP')}
                        >
                            {renderSortIcon('vkNettoP')}
                            Anteil
                        </td>
                        <td
                            className={classnames(moneyClassName, styles.vkNetto, styles.borderRight)}
                            onClick={() => toggleSort('vkNetto')}
                        >
                            {renderSortIcon('vkNetto')}
                            {filterValues.source === 'bestellung' ? 'EK Netto' : 'VK Netto'}
                        </td>
                    </>
                )}
                {filterValues.showBMarge && filterValues.source !== 'bestellung' && (
                    <>
                        <td
                            className={classnames(percentClassName, styles.bMarge)}
                            onClick={() => toggleSort('bMargePrevGrowth')}
                        >
                            {renderSortIcon('bMargePrevGrowth')}%
                        </td>
                        <td className={classnames(moneyClassName, styles.bMarge)} onClick={() => toggleSort('bMargeP')}>
                            {renderSortIcon('bMargeP')}
                            Anteil
                        </td>
                        <td className={classnames(moneyClassName, styles.bMarge)} onClick={() => toggleSort('bMarge')}>
                            {renderSortIcon('bMarge')}
                            B-Marge
                        </td>
                    </>
                )}
                {filterValues.showNMarge && filterValues.source !== 'bestellung' && (
                    <>
                        <td
                            className={classnames(percentClassName, styles.nMarge)}
                            onClick={() => toggleSort('nMargePrevGrowth')}
                        >
                            {renderSortIcon('nMargePrevGrowth')}%
                        </td>
                        <td className={classnames(moneyClassName, styles.nMarge)} onClick={() => toggleSort('nMargeP')}>
                            {renderSortIcon('nMargeP')}
                            Anteil
                        </td>
                        <td
                            className={classnames(moneyClassName, styles.nMarge, styles.borderRight)}
                            onClick={() => toggleSort('nMarge')}
                        >
                            {renderSortIcon('nMarge')}
                            N-Marge
                        </td>
                    </>
                )}
                {filterValues.showBAnteil && filterValues.source !== 'bestellung' && (
                    <>
                        <td
                            className={classnames(percentClassName, styles.bAnteil)}
                            onClick={() => toggleSort('bAnteilPrevGrowth')}
                        >
                            {renderSortIcon('bAnteilPrevGrowth')}%
                        </td>
                        <td
                            className={classnames(moneyClassName, styles.bAnteil)}
                            onClick={() => toggleSort('bAnteil')}
                        >
                            {renderSortIcon('bAnteil')}
                            B-Anteil
                        </td>
                    </>
                )}
                {auth.rolle !== 'AD' &&
                    auth.rolle !== 'EX' &&
                    filterValues.showFAnteil &&
                    filterValues.source !== 'bestellung' && (
                        <>
                            <td
                                className={classnames(percentClassName, styles.fAnteil)}
                                onClick={() => toggleSort('fAnteilPrevGrowth')}
                            >
                                {renderSortIcon('fAnteilPrevGrowth')}%
                            </td>
                            <td
                                className={classnames(moneyClassName, styles.fAnteil)}
                                onClick={() => toggleSort('fAnteil')}
                            >
                                {renderSortIcon('fAnteil')}
                                F-Anteil
                            </td>
                        </>
                    )}
            </tr>
        );
    };

    const moneyClassName = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportMoneyPrintCell : formStyles.reportMoneyCell,
    );
    const percentClassName = classnames(
        styles.tdBody,
        formStyles.dataValueRight,
        printView ? formStyles.reportPercentPrintCell : formStyles.reportPercentCell,
    );

    const renderValueWithGrowth = ({
        value,
        valueP,
        prevGrowth,
        avgGrowth,
        className,
        isMoney = true,
        borderRight = false,
        decimal = 2,
        showValueP = true,
    }: {
        value: number;
        valueP?: number;
        prevGrowth: GrowthWithColor;
        avgGrowth: GrowthWithColor;
        className: string;
        isMoney?: boolean;
        borderRight?: boolean;
        decimal?: number;
        showValueP?: boolean;
    }) => {
        const getValueS = (v: number): string => (isMoney ? formatMoney(v) : formatDecimal(v, decimal));

        return (
            <>
                <td className={classnames(percentClassName, className)}>
                    {prevGrowth.growth === undefined ? (
                        <span className={formStyles.growth}>&nbsp;</span>
                    ) : (
                        <span
                            className={classnames(formStyles.growth, prevGrowth.color)}
                            title={
                                prevGrowth.oldValue
                                    ? `Vorjahr: ${getValueS(prevGrowth.oldValue)}  Differenz: ${getValueS(
                                          prevGrowth.newValue - prevGrowth.oldValue,
                                      )}`
                                    : ''
                            }
                        >
                            {formatPercent(prevGrowth.growth, 0)}
                        </span>
                    )}
                    <br />
                    {avgGrowth.growth === undefined ? (
                        <span className={formStyles.growth}>&nbsp;</span>
                    ) : (
                        <span
                            className={classnames(formStyles.growth, avgGrowth.color)}
                            title={
                                avgGrowth.oldValue
                                    ? `Durchschnitt 3J: ${getValueS(avgGrowth.oldValue)}  Differenz: ${getValueS(
                                          avgGrowth.newValue - avgGrowth.oldValue,
                                      )}`
                                    : ''
                            }
                        >
                            {formatPercent(avgGrowth.growth, 0)}
                        </span>
                    )}
                </td>
                {showValueP && (
                    <td className={classnames(moneyClassName, className)}>{formatPercent(valueP || 0, 1)}</td>
                )}
                <td
                    className={classnames(moneyClassName, className, {
                        [styles.borderRight]: borderRight,
                    })}
                >
                    {getValueS(value)}
                </td>
            </>
        );
    };

    const renderLine = (line: LineDataWithGrowth, strong = false) => {
        const header = (
            <>
                {line.groupByLink && !printView ? (
                    <OpenLink link={line.groupByLink} label={line.groupByLabel} />
                ) : (
                    <div>{line.groupByLabel}</div>
                )}
                {line.groupByNote && <div className={styles.note}>{line.groupByNote}</div>}
            </>
        );

        return (
            <tr key={source + '|' + groupBy + '|' + line.groupById}>
                <td className={styles.tdBody}>{strong ? <strong>{header}</strong> : header}</td>
                {renderValueWithGrowth({
                    value: line.count,
                    valueP: line.countP,
                    prevGrowth: line.countPrevGrowth,
                    avgGrowth: line.countAvgGrowth,
                    className: styles.count,
                    isMoney: false,
                    decimal: 1,
                    borderRight: true,
                })}
                {filterValues.showEkNetto &&
                    filterValues.source !== 'bestellung' &&
                    renderValueWithGrowth({
                        value: line.ekNetto,
                        valueP: line.ekNettoP,
                        prevGrowth: line.ekNettoPrevGrowth,
                        avgGrowth: line.ekNettoAvgGrowth,
                        className: styles.ekNetto,
                    })}
                {filterValues.showVkNetto &&
                    renderValueWithGrowth({
                        value: line.vkNetto,
                        valueP: line.vkNettoP,
                        prevGrowth: line.vkNettoPrevGrowth,
                        avgGrowth: line.vkNettoAvgGrowth,
                        className: styles.vkNetto,
                        borderRight: true,
                    })}
                {filterValues.showBMarge &&
                    filterValues.source !== 'bestellung' &&
                    renderValueWithGrowth({
                        value: line.bMarge,
                        valueP: line.bMargeP,
                        prevGrowth: line.bMargePrevGrowth,
                        avgGrowth: line.bMargeAvgGrowth,
                        className: styles.bMarge,
                    })}
                {filterValues.showNMarge &&
                    filterValues.source !== 'bestellung' &&
                    renderValueWithGrowth({
                        value: line.nMarge,
                        valueP: line.nMargeP,
                        prevGrowth: line.nMargePrevGrowth,
                        avgGrowth: line.nMargeAvgGrowth,
                        className: styles.nMarge,
                        borderRight: true,
                    })}
                {filterValues.showBAnteil &&
                    filterValues.source !== 'bestellung' &&
                    renderValueWithGrowth({
                        value: line.bAnteil,
                        prevGrowth: line.bAnteilPrevGrowth,
                        avgGrowth: line.bAnteilAvgGrowth,
                        className: styles.bAnteil,
                        showValueP: false,
                    })}
                {auth.rolle !== 'AD' &&
                    auth.rolle !== 'EX' &&
                    filterValues.showFAnteil &&
                    filterValues.source !== 'bestellung' &&
                    renderValueWithGrowth({
                        value: line.fAnteil,
                        prevGrowth: line.fAnteilPrevGrowth,
                        avgGrowth: line.fAnteilAvgGrowth,
                        className: styles.fAnteil,
                        showValueP: false,
                    })}
            </tr>
        );
    };

    if (!lookupsLoadingFinished) {
        return <div />;
    }

    return (
        <div className={printView ? styles.printContainer : styles.container}>
            {(umsatzSummaryJ0.loading ||
                umsatzSummaryJ1.loading ||
                umsatzSummaryJ2.loading ||
                umsatzSummaryJ3.loading) && <LoadingIndicator />}
            {printView && (
                <div
                    dangerouslySetInnerHTML={{
                        __html:
                            // eslint-disable-next-line max-len
                            '<style>html, body, #root { overflow: unset !important; height: unset !important; }</style>',
                    }}
                />
            )}
            {!printView && (
                <table className={formStyles.reportFixedHeaderNoBgColor}>
                    <thead>{renderHeaderRow()}</thead>
                </table>
            )}
            <h1>
                Umsatzübersicht - {sourceOptions.find((o) => o.value === source)?.label || '?'} nach{' '}
                {groupByOptions.find((o) => o.value === groupBy)?.label}
                {' (' + moment(datumVon).format('DD.MM.YYYY') + ' bis ' + moment(datumBis).format('DD.MM.YYYY') + ')'}
            </h1>
            {printView && <FilterInfo filterValues={filterValues} />}
            <table className={formStyles.reportTable}>
                <thead>{renderHeaderRow()}</thead>
                <tbody>
                    <React.Fragment key="topSummary">{renderLine(umsatzSummaryLine, true)}</React.Fragment>
                    <tr>
                        <td colSpan={22} className={classnames(styles.borderBottom, styles.noPadding)} />
                    </tr>
                    {sortedTableData.map((line) => renderLine(line))}
                    <tr>
                        <td colSpan={22} className={classnames(styles.borderBottom, styles.noPadding)} />
                    </tr>
                    <React.Fragment key="bottomSummary">{renderLine(umsatzSummaryLine, true)}</React.Fragment>
                </tbody>
            </table>
        </div>
    );
};
